import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  type AnyRoute,
} from "@tanstack/react-router"
import { lazy, Suspense, type ComponentType, type FunctionComponent, type ReactNode } from "react"
import { AppProviders } from "@/app-providers"
import AuthLayout from "@/app/(auth)/layout"
import DashboardLayout from "@/app/dashboard/layout"
import ReportsLayout from "@/app/dashboard/reports/layout"
import SettingsLayout from "@/app/dashboard/settings/layout"

type PageModule = { default: ComponentType }
type PageLoader = () => Promise<PageModule>

const pages = import.meta.glob<PageModule>("./app/**/page.tsx")

function LoadingPage() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  )
}

function RootRoute() {
  return (
    <AppProviders>
      <Outlet />
    </AppProviders>
  )
}

function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center p-6 text-center">
      <div>
        <h1 className="text-2xl font-bold">Page not found</h1>
        <a className="mt-3 inline-block text-primary underline" href="/">
          Return home
        </a>
      </div>
    </div>
  )
}

function lazyPage(loader: PageLoader): FunctionComponent {
  const Page = lazy(loader)
  return function LazyPage() {
    return (
      <Suspense fallback={<LoadingPage />}>
        <Page />
      </Suspense>
    )
  }
}

function withLayout(Page: ComponentType, Layout: ComponentType<{ children: ReactNode }>): FunctionComponent {
  return function LayoutRoute() {
    return (
      <Layout>
        <Page />
      </Layout>
    )
  }
}

function normalizedPagePath(file: string): string {
  return file
    .replace(/^\.\/app\//, "")
    .replace(/(^|\/)page\.tsx$/, "")
    .replace(/\([^/]+\)\//g, "")
    .replace(/\[\.\.\.[^\]]+\]/g, "$")
    .replace(/\[([^\]]+)\]/g, "$$$1")
}

const rootRoute = createRootRoute({
  component: RootRoute,
  notFoundComponent: NotFoundPage,
})

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "dashboard",
  component: () => (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  ),
})

const reportsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "reports",
  component: () => (
    <ReportsLayout>
      <Outlet />
    </ReportsLayout>
  ),
})

const settingsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: "settings",
  component: () => (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  ),
})

function addPage(parent: AnyRoute, path: string, component: FunctionComponent, target: AnyRoute[]): void {
  target.push(createRoute({
    getParentRoute: () => parent,
    path: path || "/",
    component,
  }))
}

const rootPages: AnyRoute[] = []
const dashboardPages: AnyRoute[] = []
const reportPages: AnyRoute[] = []
const settingsPages: AnyRoute[] = []

for (const [file, loader] of Object.entries(pages)) {
  const isAuth = file.startsWith("./app/(auth)/")
  const path = normalizedPagePath(file)
  const Page = lazyPage(loader)

  if (path === "") {
    addPage(rootRoute, "", Page, rootPages)
  } else if (isAuth) {
    addPage(rootRoute, path, withLayout(Page, AuthLayout), rootPages)
  } else if (path === "health") {
    addPage(rootRoute, path, Page, rootPages)
  } else if (path === "dashboard/reports" || path.startsWith("dashboard/reports/")) {
    addPage(reportsRoute, path.slice("dashboard/reports".length).replace(/^\//, ""), Page, reportPages)
  } else if (path === "dashboard/settings" || path.startsWith("dashboard/settings/")) {
    addPage(settingsRoute, path.slice("dashboard/settings".length).replace(/^\//, ""), Page, settingsPages)
  } else if (path === "dashboard" || path.startsWith("dashboard/")) {
    addPage(dashboardRoute, path.slice("dashboard".length).replace(/^\//, ""), Page, dashboardPages)
  } else {
    addPage(rootRoute, path, Page, rootPages)
  }
}

const routeTree = rootRoute.addChildren([
  ...rootPages,
  dashboardRoute.addChildren([
    ...dashboardPages,
    reportsRoute.addChildren(reportPages),
    settingsRoute.addChildren(settingsPages),
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}
